import { useEffect, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { getStripe } from "@/lib/stripe";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { firstChargeLabel, signupPricingSentence } from "@/lib/pricing";

interface Props {
  email: string;
  returnPath?: string;
  promoCode?: string;
  isFullDiscount?: boolean;
  isHalfPromo?: boolean;
  onError?: (msg: string) => void;
}

const PayForm = ({ returnPath, onError }: { returnPath: string; onError?: (msg: string) => void }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}${returnPath}?subscription=success`,
      },
    });
    if (error) {
      onError?.(error.message || "Payment failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentElement options={{ layout: "tabs" }} />
      <Button type="button" onClick={handleSubmit} disabled={!stripe || submitting} className="w-full h-11 font-semibold">
        {submitting ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing…</>) : "Subscribe"}
      </Button>
      <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground pt-1">
        <span>Powered by</span>
        <span className="font-semibold tracking-tight text-[#635BFF]">Stripe</span>
      </div>
    </div>
  );
};

const CustomSignupCheckout = ({ email, returnPath = "/auth", promoCode, isFullDiscount, isHalfPromo, onError }: Props) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [noPaymentRequired, setNoPaymentRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setClientSecret(null);
    setNoPaymentRequired(false);
    setErrorMsg(null);

    (async () => {
      try {
        const [{ data, error }, stripe] = await Promise.all([
          supabase.functions.invoke("create-subscription", {
            body: { email, promo_code: promoCode || undefined },
          }),
          getStripe(),
        ]);
        if (cancelled) return;
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (data?.no_payment_required) {
          setNoPaymentRequired(true);
          setStripePromise(Promise.resolve(stripe));
          return;
        }
        if (!data?.client_secret) throw new Error("No client secret returned");
        setStripePromise(Promise.resolve(stripe));
        setClientSecret(data.client_secret);
      } catch (e: any) {
        const msg = e?.message || "Could not start payment";
        setErrorMsg(msg);
        onError?.(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [email, promoCode]);

  const summaryBox = (
    <div className="rounded-lg border bg-muted/30 p-4 flex items-center justify-between">
      <div>
        <div className="text-sm font-semibold">Hound Pro</div>
        <div className="text-xs text-muted-foreground">
          Billed monthly on the 1st · cancel anytime{promoCode ? ` · code ${promoCode.toUpperCase()} applied` : ""}
        </div>
      </div>
      <div className="text-right">
        <div className="text-2xl font-bold">{firstChargeLabel({ isFullDiscount, isHalfPromo })}</div>
        <div className="text-xs text-muted-foreground">today</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-8">
        {summaryBox}
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="space-y-8">
        {summaryBox}
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {errorMsg}
        </div>
      </div>
    );
  }

  if (noPaymentRequired) {
    return (
      <div className="space-y-8">
        {summaryBox}
        <div className="rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
          No payment required with this code. You can sign in once your account is confirmed.
        </div>
      </div>
    );
  }

  if (!clientSecret || !stripePromise) return null;

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: { theme: "stripe" },
  };

  return (
    <div className="space-y-8">
      {summaryBox}
      <Elements stripe={stripePromise} options={options}>
        <PayForm returnPath={returnPath} onError={onError} />
      </Elements>
    </div>
  );
};

export default CustomSignupCheckout;
